import { useState, useEffect } from "react";
import { TextField } from "@mui/material";
import { useForm } from "../../hooks/useForm/useForm";
import { Database, supabase } from "../../../../lib/supabase";
import { FormProps } from "../form-types";
import { CustomTextField } from "../form-input-components/custom-input-components/custom-text-field.component";
import {
	SupabaseTableEnum,
	SupabaseWriteTableEnum,
} from "../../../../lib/supabase/supabaseTypes";
import { Logger } from "../../../../lib/logger/Logger";
import { CitySelect } from "../form-input-components/selects/city-select/city-select";
import { useToast } from "../../context/ToastContext";
import { useCentralStore } from "../../store/Central";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import { ABRECHNUNGSBEREICH_OPTIONS } from "../../../../lib/german-xml/types";
import { hasRegion } from "../../lib/utils/region-utils";
import { Region } from "../../types/enums";
import { CountryEnum } from "@/lib/supabase/supabaseEnums";
import { CountrySelect } from "../form-input-components/selects/country-select/country.select";
import { showNotification } from "../../store/Central/selectors";

export const CompanyInformationForm: React.FC<FormProps> = ({
	submitRef = null,
}) => {
	const { organization, getOrganization, activatedRegions } = useCentralStore(
		(state) => ({
			organization: state.organization,
			getOrganization: state.getOrganization,
			activatedRegions: state.activatedRegions,
		})
	);
	const [brandName, setBrandName] = useState("");
	const [brandId, setBrandId] = useState<number | null>(null);

	useEffect(() => {
		if (!organization?.id) return;

		supabase
			.from(SupabaseTableEnum.BRANDS)
			.select("id, name")
			.eq("organization_id", organization.id)
			.single()
			.then(({ data }) => {
				if (data) {
					setBrandId(data.id);
					setBrandName(data.name ?? "");
				}
			});
	}, [organization?.id]);

	const {
		handleInputChange,
		handleValueChange,
		handleUpdate,
		formData,
		formErrors,
	} = useForm<
		Database["public"]["Tables"][SupabaseWriteTableEnum.ORGANIZATIONS]["Update"]
	>(
		{},
		SupabaseWriteTableEnum.ORGANIZATIONS,
		true,
		organization?.id,
		"Firmenangaben",
		() => {}
	);

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		if (!organization?.id) {
			showNotification({
				message: "Es ist ein Fehler aufgetreten.",
				type: "error",
				description: "Keine Organisations-ID gefunden.",
			});
			Logger.error("No organizationId found in CompanyInformationForm");
			return;
		}

		const [orgResult, brandResult] = await Promise.all([
			handleUpdate(event, organization.id),
			brandId
				? supabase
						.from(SupabaseTableEnum.BRANDS)
						.update({ name: brandName })
						.eq("id", brandId)
				: Promise.resolve({ error: null }),
		]);

		if (!orgResult.error && !brandResult.error) {
			showNotification({
				message: "Ihre Änderungen wurden gespeichert.",
				type: "success",
			});
		} else {
			showNotification({
				message: "Es ist ein Fehler aufgetreten.",
				type: "error",
				description: `${orgResult.error ?? ""} ${
					brandResult.error?.message ?? ""
				}`,
			});
			Logger.error(
				orgResult.error ?? brandResult.error,
				{},
				"Error in CompanyInformationForm"
			);
		}

		await getOrganization();
	};

	return (
		<form onSubmit={onSubmit}>
			<button ref={submitRef} type="submit" style={{ display: "none" }} />
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					width: "500px",
					paddingTop: "1rem",
				}}
			>
				<TextField
					label="Laborname"
					name="name"
					helperText="Der Name Ihres Labors, wie er in der Navigation angezeigt wird."
					value={formData.name || ""}
					onChange={handleInputChange}
				/>
				<TextField
					label="Anzeigename"
					helperText="Dieser Name wird Ihren Kunden angezeigt."
					value={brandName}
					onChange={(e) => setBrandName(e.target.value)}
				/>
				<TextField
					label="Anrede/Firma"
					name="title_or_company_name"
					helperText="Ihre Anrede (Frau, Herr...) oder Firmenbezeichnung."
					value={formData.title_or_company_name || ""}
					onChange={handleInputChange}
				/>
				<TextField
					label="Name/Zusatz"
					name="name_or_additional_information"
					helperText="Ihr Vorname und Name."
					value={formData.name_or_additional_information || ""}
					onChange={handleInputChange}
				/>
				<TextField
					label="Strasse"
					name="street"
					helperText="Die Strasse und Hausnummer Ihrer Anschrift."
					value={formData.street || ""}
					onChange={handleInputChange}
				/>
				<div
					style={{
						paddingBottom: "8px",
					}}
				>
					<CitySelect
						onChange={(value) => {
							handleValueChange("city", value.city);
							handleValueChange("postal_code", value.postalCode);
						}}
						value={formData.postal_code + " " + formData.city}
					/>
				</div>
				<CountrySelect
					value={formData.country || ""}
					onChange={(value) => handleValueChange("country", value)}
				/>
				<CustomTextField
					label="Telefon/Fax"
					name="phone_and_fax"
					helperText="Ihre Telefon- und Faxnummer"
					value={formData.phone_and_fax || ""}
					onChange={handleInputChange}
					validationError={formErrors.phone_and_fax}
				/>
				<CustomTextField
					key="gln"
					label="GLN"
					name="gln"
					helperText="GLN, Global Location Number, wird verwendet, um Ihre Laboradresse eindeutig zu kennzeichnen."
					value={formData.gln || ""}
					onChange={handleInputChange}
					validationError={formErrors.gln}
				/>
				{hasRegion(Region.DE, activatedRegions) &&
					organization?.country === CountryEnum.GERMANY && (
						<div
							style={{
								paddingBottom: "16px",
								paddingTop: "16px",
							}}
						>
							<Label htmlFor="billing_region">
								Abrechnungsbereich
							</Label>
							<Select
								value={formData.billing_region || "NONE"}
								onValueChange={(value) =>
									handleValueChange(
										"billing_region",
										value !== "NONE" ? value : null
									)
								}
							>
								<SelectTrigger
									id="billing_region"
									className="mt-1"
								>
									<SelectValue placeholder="Abrechnungsbereich wählen..." />
								</SelectTrigger>
								<SelectContent>
									{ABRECHNUNGSBEREICH_OPTIONS.map(
										(option) => (
											<SelectItem
												key={option.value}
												value={option.value}
											>
												{option.value !== "NONE" &&
													option.value + " - "}{" "}
												{option.label}
											</SelectItem>
										)
									)}
								</SelectContent>
							</Select>
							<p className="text-xs text-gray-500 mt-1">
								Erforderlich für den deutschen XML-Export. Nur
								auswählen, wenn das Labor in Deutschland
								ansässig ist.
							</p>
						</div>
					)}
			</div>
			<Button type="submit">Speichern</Button>
		</form>
	);
};
